import {
  resolveTaskTypeMetrics,
  type TaskMetricAgingBucket,
  type TaskTypeMetric,
  type TaskTypeMetricKind,
} from '@erp/shared-types';
import type { TaskTypeMetricCard } from '@erp/rpc';
import { formatCurrency, formatNumber } from '@/utils/formatters';

/**
 * Pure rules for the Task Page metric strip: which cards to show (the loaded
 * ones, or the type's configured ones as placeholders while loading), their
 * titles' inputs and formatted values.
 */

/** Open-task counts per age bucket (days since the task was created). */
export type TaskAgingCounts = Record<TaskMetricAgingBucket, number>;

export const EMPTY_TASK_AGING: TaskAgingCounts = {
  '0-2': 0,
  '3-7': 0,
  '8-14': 0,
  '15+': 0,
};

/** The input-schema facts a sum card's title and format come from. */
export interface MetricInputField {
  fieldName: string;
  fieldType: string;
  label: string;
}

export type TaskMetricCardView =
  | {
      key: string;
      kind: 'aging';
      label: string | null;
      buckets: TaskAgingCounts;
    }
  | {
      key: string;
      kind: Exclude<TaskTypeMetricKind, 'aging'>;
      label: string | null;
      /** Sum cards: the input field's label (or name when it has none). */
      fieldLabel: string | null;
      display: string;
    };

function findField(
  field: string | undefined,
  inputFields: readonly MetricInputField[],
): MetricInputField | undefined {
  return field === undefined
    ? undefined
    : inputFields.find((candidate) => candidate.fieldName === field);
}

/** Currency fields format as money; every other numeric field as a number. */
export function formatTaskMetricValue(
  value: number | undefined,
  fieldType: string | undefined,
): string {
  const amount = value ?? 0;
  return fieldType === 'currency'
    ? formatCurrency(amount, { currency: 'USD' })
    : formatNumber(amount);
}

function toView(
  card: TaskTypeMetricCard,
  index: number,
  inputFields: readonly MetricInputField[],
): TaskMetricCardView {
  const key = `${index}:${card.kind}:${card.field ?? ''}`;
  if (card.kind === 'aging') {
    return {
      key,
      kind: 'aging',
      label: card.label,
      buckets: card.buckets ?? EMPTY_TASK_AGING,
    };
  }
  const field = findField(card.field, inputFields);
  return {
    key,
    kind: card.kind,
    label: card.label,
    fieldLabel:
      card.kind === 'sum' ? field?.label.trim() || (card.field ?? null) : null,
    display: formatTaskMetricValue(
      card.value,
      card.kind === 'sum' ? field?.fieldType : undefined,
    ),
  };
}

/** Placeholder cards for a type's configured metrics, before values load. */
export function placeholderMetricCards(
  metrics: readonly TaskTypeMetric[] | null | undefined,
): TaskTypeMetricCard[] {
  return resolveTaskTypeMetrics(metrics).map((metric) => ({
    kind: metric.kind,
    label: metric.label ? metric.label : null,
    field: metric.kind === 'sum' ? metric.field : undefined,
  }));
}

/** The strip's cards: the loaded values, else placeholders from the config. */
export function taskMetricCardViews({
  loaded,
  configured,
  inputFields,
}: {
  loaded: readonly TaskTypeMetricCard[] | undefined;
  configured: readonly TaskTypeMetric[] | null | undefined;
  inputFields: readonly MetricInputField[];
}): TaskMetricCardView[] {
  const cards = loaded ?? placeholderMetricCards(configured);
  return cards.map((card, index) => toView(card, index, inputFields));
}

const GRID_COLUMNS: Record<number, string> = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  5: 'xl:grid-cols-5',
  6: 'xl:grid-cols-6',
};

/** One row of cards on wide screens, whatever the type configured. */
export function taskMetricGridClass(count: number): string {
  const columns = GRID_COLUMNS[Math.min(Math.max(count, 1), 6)];
  return `grid grid-cols-1 gap-3 md:grid-cols-2 ${columns ?? ''}`;
}
